import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { NotificationItem } from './NotificationItem';
import { inAppNotificationService } from '../../services';
import type { InAppNotification } from '../../types/notification.types';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange: (count: number) => void;
}

/**
 * Notification Dropdown Component
 * Displays list of notifications with infinite scroll
 */
export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  onUnreadCountChange,
}) => {
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (loading) return;

      setLoading(true);
      try {
        const response = await inAppNotificationService.getNotifications({
          page: pageNum,
          limit: 20,
        });

        const newNotifications = response.data || [];
        
        if (append) {
          setNotifications((prev) => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }

        setHasMore(pageNum < (response.pagination?.pages || 1));
        setPage(pageNum);

        // Update unread count
        if (response.unreadCount !== undefined) {
          onUnreadCountChange(response.unreadCount);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
      }
    },
    [loading, onUnreadCountChange]
  );

  // Initial fetch when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(1, false);
    }
  }, [isOpen]);

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchNotifications(page + 1, true);
        }
      },
      { threshold: 1.0 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [isOpen, hasMore, loading, page, fetchNotifications]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Mark as read
  const handleMarkAsRead = async (id: string) => {
    try {
      await inAppNotificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, status: 'READ' as const } : n))
      );
      // Refresh unread count
      const response = await inAppNotificationService.getUnreadCount();
      onUnreadCountChange(response);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    setMarkingAllRead(true);
    try {
      await inAppNotificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'READ' as const })));
      onUnreadCountChange(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setMarkingAllRead(false);
    }
  };

  // Archive notification
  const handleArchive = async (id: string) => {
    try {
      await inAppNotificationService.archiveNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      // Refresh unread count if it was unread
      const notification = notifications.find((n) => n._id === id);
      if (notification?.status === 'UNREAD') {
        const response = await inAppNotificationService.getUnreadCount();
        onUnreadCountChange(response);
      }
    } catch (error) {
      console.error('Failed to archive notification:', error);
    }
  };

  // Delete notification
  const handleDelete = async (id: string) => {
    try {
      await inAppNotificationService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      // Refresh unread count if it was unread
      const notification = notifications.find((n) => n._id === id);
      if (notification?.status === 'UNREAD') {
        const response = await inAppNotificationService.getUnreadCount();
        onUnreadCountChange(response);
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Group notifications by date
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const date = new Date(notification.createdOn);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    let label: string;
    if (diffDays === 0) {
      label = 'Today';
    } else if (diffDays === 1) {
      label = 'Yesterday';
    } else if (diffDays < 7) {
      label = 'This Week';
    } else {
      label = 'Older';
    }

    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(notification);
    return groups;
  }, {} as Record<string, InAppNotification[]>);

  const groupOrder = ['Today', 'Yesterday', 'This Week', 'Older'];

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={`absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg border shadow-2xl overflow-hidden z-50 ${
        theme === 'light'
          ? 'bg-white border-slate-200'
          : 'bg-slate-800 border-slate-600'
      }`}
      style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <div
        className={`px-4 py-3 border-b flex items-center justify-between ${
          theme === 'light'
            ? 'border-slate-200 bg-gradient-to-r from-sky-50 to-cyan-50'
            : 'border-slate-700 bg-slate-700/50'
        }`}
      >
        <h3
          className={`text-sm font-semibold ${
            theme === 'light' ? 'text-slate-900' : 'text-white'
          }`}
        >
          Notifications
        </h3>
        {notifications.some((n) => n.status === 'UNREAD') && (
          <button
            onClick={handleMarkAllAsRead}
            disabled={markingAllRead}
            className={`text-xs font-medium transition-colors ${
              theme === 'light'
                ? 'text-sky-600 hover:text-sky-700'
                : 'text-cyan-400 hover:text-cyan-300'
            } disabled:opacity-50`}
          >
            {markingAllRead ? 'Marking...' : 'Mark all read'}
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {notifications.length === 0 && !loading ? (
          <div className="px-4 py-12 text-center">
            <svg
              className={`w-12 h-12 mx-auto mb-3 ${
                theme === 'light' ? 'text-slate-300' : 'text-slate-600'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            <p
              className={`text-sm ${
                theme === 'light' ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              No notifications yet
            </p>
          </div>
        ) : (
          groupOrder.map(
            (label) =>
              groupedNotifications[label] && (
                <div key={label}>
                  {/* Group Label */}
                  <div
                    className={`px-4 py-2 text-xs font-semibold sticky top-0 z-10 ${
                      theme === 'light'
                        ? 'bg-slate-100 text-slate-600'
                        : 'bg-slate-700/80 text-slate-300'
                    }`}
                  >
                    {label}
                  </div>

                  {/* Notifications in this group */}
                  {groupedNotifications[label].map((notification) => (
                    <NotificationItem
                      key={notification._id}
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      onArchive={handleArchive}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )
          )
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="px-4 py-3 text-center">
            <div
              className={`inline-block w-5 h-5 border-2 border-t-transparent rounded-full animate-spin ${
                theme === 'light' ? 'border-sky-500' : 'border-cyan-400'
              }`}
            />
          </div>
        )}

        {/* Infinite scroll trigger */}
        {hasMore && !loading && notifications.length > 0 && (
          <div ref={observerTarget} className="h-4" />
        )}

        {/* No more notifications */}
        {!hasMore && notifications.length > 0 && (
          <div className="px-4 py-3 text-center">
            <p
              className={`text-xs ${
                theme === 'light' ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              No more notifications
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
